// Slot configuration for the DEFINE and RUN sections introduced in Epic 0
// (split campaign definition from execution). Each slot is a sub-nav entry; for
// now its body is a MigrationStub that links to where the artefact currently
// lives. Later epic stories replace those stubs with the migrated editors, so
// labels, paths, and current locations all live here in one place.

export interface SectionSlot {
  // Route path relative to the section (e.g. "brief" under "define").
  to: string;
  label: string;
  blurb: string;
  // Where this artefact currently lives, relative to /campaigns/:id. Omitted
  // when nothing exists yet (the slot is purely forward-looking scaffolding).
  currentPath?: string;
  // The slot the section index redirects to. Defaults to the first slot when
  // no slot is flagged (e.g. RUN lands on Pipeline though Pool is listed first).
  isDefault?: boolean;
}

// The path the section's index route should redirect to: the flagged default
// slot, or the first slot when none is flagged.
export function defaultSlotPath(slots: SectionSlot[]): string {
  return (slots.find((slot) => slot.isDefault) ?? slots[0]).to;
}

export const DEFINE_SLOTS: SectionSlot[] = [
  {
    to: "brief",
    label: "Brief",
    blurb: "The role brief and what good looks like.",
  },
  {
    to: "blueprint",
    label: "Blueprint",
    blurb: "Role blueprint and capability dimensions.",
  },
  {
    to: "selection-process",
    label: "Selection Process",
    blurb: "How we will assess and move candidates through the search.",
  },
  {
    to: "market-map",
    label: "Market Map",
    blurb: "Market map and the boolean search strings that source candidates.",
  },
  {
    to: "assessment",
    label: "Assessment",
    blurb: "Assessment matrix, assessment guide, and scoring-matrix templates.",
  },
];

// Sub-navigation inside the RUN › Pipeline slot (#2218). The pipeline absorbs
// several stepper phases, so it carries its own third-level nav rather than
// stacking everything on one page.
export const PIPELINE_SLOTS: SectionSlot[] = [
  {
    to: "overview",
    label: "Overview",
    blurb: "Funnel, snapshot, and the full candidate pipeline table.",
    isDefault: true,
  },
  {
    to: "longlist",
    label: "Longlist",
    blurb: "Candidates promoted from the pool into the active search.",
  },
  {
    to: "scoring",
    label: "Scoring",
    blurb: "Recruiter assessment scoring against the capability dimensions.",
  },
  {
    to: "shortlist",
    label: "Shortlist",
    blurb: "Finalists presented to the client.",
  },
];

export const RUN_SLOTS: SectionSlot[] = [
  {
    to: "discovery",
    label: "Discovery",
    blurb: "Run X-Ray searches and browse discovered LinkedIn profiles.",
  },
  {
    to: "pool",
    label: "Pool",
    blurb: "Candidates sourced into the campaign.",
  },
  {
    to: "pipeline",
    label: "Pipeline",
    blurb: "Candidates moving through the search.",
    isDefault: true,
  },
  {
    to: "outreach",
    label: "Outreach",
    blurb: "Messaging candidates and posting the role.",
  },
  {
    to: "interviews",
    label: "Interviews",
    blurb: "Client interviews and feedback.",
  },
  {
    to: "offers",
    label: "Offers",
    blurb: "Offers, closing, and placement.",
  },
];
